import React from 'react';

interface SwitchProps {
    /**
     * JSXCases
     */
    children: JSX.Element | JSX.Element[];
}

type CaseProps = {
    /**
     * Whether the children component should be rendered
     */
    condition?: boolean;
    /**
     * Will be rendered if no other conditions are JSXCases are true. Should
     * be placed at the bottom like a default switch case
     */
    default?: boolean;
    /**
     * Content to render
     */
    children: React.ReactNode;
} & (
        | {
            condition: boolean;
            default?: never;
        }
        | {
            condition?: never;
            default: boolean;
        }
    );

/**
 * This is a switch statement for JSXCase components.  The first that evaulates to true is built.
 * All others are ignored.
 *
 * Example use:
 * <JSXSwitch>
 *    <JSXCase Condition={false}>
 *        //I will not render cause I am false.
 *    </JSXCase>
 *    <JSXCase Condition={true}>
 *        //I will render cause I am the first true.
 *    </JSXCase>
 *    <JSXCase Condition={true}>
 *        //I will not render cause I am not the first true.
 *    </JSXCase>
 *    <JSXCase Default>
 *        //I will render by default if no previous condition is true.
 *    </JSXCase>
 * </JSXSwitch>
 */
export const JSXSwitch: React.FC<SwitchProps> = (props) => {
    let matchingElementFound: boolean = false;
    let matchingElement: JSX.Element | null = null;

    React.Children.forEach(props.children, function (element) {
        const caseProps: CaseProps = element.props;
        if (!matchingElementFound && (caseProps.default || caseProps.condition)) {
            matchingElementFound = true;
            matchingElement = element;
        }
    });

    if (!matchingElementFound) {
        return null;
    }

    return matchingElement;
};

export const JSXCase: React.FC<CaseProps> = (props) => {
    return <>{props.children}</>;
};